import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { Icon, useEscapeEbene, useMenuTasten } from "@kundeninfo/ui";
import { routeToPath } from "../router.js";
import type { Route } from "../router.js";
import type { Zuschnitt } from "../zuschnitt.js";
import "./Navigation.css";

/** Die Ziele, die die Hauptnavigation ansteuern kann — seit dem Paket „Menü komplett"
 *  (05.09.) sind das ALLE Einträge der Leiste. */
export type NavigationsZiel =
  | "kunden" | "angebote" | "auftraege" | "rechnungen" | "artikel" | "schnittstellen"
  | "verwaltung" | "einstellungen"
  // Der einzige Bereich des Zuschnitts „belegdesign" (siehe `bereicheFuer`).
  | "belegdesign";

export type TableisteProps = {
  /** Der Eintrag, auf dem die Anwendung gerade steht. */
  aktiv: NavigationsZiel;
  onNavigate: (ziel: NavigationsZiel) => void;
  /** Die Einträge — Vorgabe `BEREICHE`; im Zuschnitt `bereicheFuer(zuschnitt)`. */
  eintraege?: Eintrag[];
};

export type Eintrag = {
  id: string; label: string; icon: string; fertig: boolean; ziel?: NavigationsZiel;
};

/**
 * Die Adresse hinter einem Hauptnavigationspunkt.
 *
 * Sie steht HIER und nicht in App.tsx, seit die Kopfleiste die Einträge selbst zeichnet
 * (10.09.2026): Ein Bereich in der Kopfleiste ist ein echter Link mit `href`, damit
 * Mittelklick und „in neuem Reiter öffnen" gehen — und dafür muss die Kopfzeile die
 * Adresse eines Eintrags erfragen können, ohne dabei über App.tsx zu gehen (das sie
 * selbst zeichnet). Die Tabelle der Einträge und die Tabelle ihrer Adressen gehören
 * ohnehin zusammen; sie standen nur aus Gewohnheit in zwei Dateien.
 *
 * Vollständig aufgezählt statt mit einem `sonst`-Zweig: Kommt ein Punkt hinzu und wird
 * hier vergessen, meldet das der Typprüfer. Ein Rückfall („alles Übrige zur Kundenliste")
 * schickte ihn stattdessen wortlos auf einen falschen Bildschirm — und niemand sähe je
 * einen Fehler.
 */
export function zielRoute(ziel: NavigationsZiel): Route {
  switch (ziel) {
    case "kunden": return { name: "kundenliste" };
    case "angebote": return { name: "angebote" };
    case "auftraege": return { name: "auftraege" };
    case "rechnungen": return { name: "rechnungen" };
    case "artikel": return { name: "artikel" };
    case "schnittstellen": return { name: "schnittstellen" };
    // Die Verwaltung öffnet ihre ÜBERSICHT — das Bereichs-Untermenü ohne Auswahl (Etappe 2,
    // Aufgabe 1). Bis dahin sprang der Menüpunkt fest auf die Benutzer; das war der einzige
    // Weg, solange die Bereiche als Tab-Leiste ÜBER dem Inhalt sassen und ein Bildschirm
    // ohne Inhalt sinnlos gewesen wäre. Mit dem Untermenü in der Spalte gilt hier dieselbe
    // Regel wie bei den vier Objektbereichen: Der Menüpunkt führt auf die Auswahl, nicht in
    // ein herausgegriffenes Ziel.
    case "verwaltung": return { name: "verwaltung" };
    case "einstellungen": return { name: "einstellungen" };
    // Der Belegdesigner IST der Layout-Bereich der Verwaltung; das Arbeitsunternehmen
    // belegt er selbst vor (views/verwaltung/layout.tsx).
    case "belegdesign": return { name: "verwaltung", bereich: "layout" };
  }
}

/** Die Adresse eines Navigationspunktes als Pfad — für das `href` echter Links. */
export function zielPfad(ziel: NavigationsZiel): string {
  return routeToPath(zielRoute(ziel));
}

/**
 * Die Hauptnavigation der Anwendung — nicht zu verwechseln mit den Tabs der Kundenakte
 * (KundenAkte.tsx), die innerhalb eines Kunden liegen.
 *
 * Sie steht seit dem 10.09.2026 in ZWEI Gruppen da (Entscheid Dirk, Design-Gespräch), und
 * das ist keine Sortierung, sondern eine Aussage über die Sache: `BEREICHE` sind die fünf
 * Orte, an denen gearbeitet wird — sie stehen als Reihe offen in der Kopfleiste und auf
 * dem Telefon in der unteren Leiste. `EINSTELLUNGEN` sind die drei Orte, an denen die
 * Anwendung selbst eingerichtet wird — sie hängen hinter dem Zahnrad rechts oben, weil man
 * dort selten hingeht und nie mitten in einem Vorgang.
 *
 * BEIDE Gruppen stehen HIER und nirgends sonst. Bis zum 10.09.2026 speiste diese eine
 * Tabelle die linke Randspalte und die untere Leiste; jetzt speist sie die Kopfleisten-
 * Reihe, das Zahnrad-Menü und die untere Leiste. Eine zweite Liste in der Kopfzeile hätte
 * genau die Abweichung zugelassen, die man erst bemerkt, wenn ein Eintrag an einer Stelle
 * fehlt.
 *
 * `fertig` heisst „dieses Modul gibt es schon"; welcher Eintrag hervorgehoben ist, sagt
 * dagegen `aktiv` von aussen. Die beiden liefen bis Paket 2b in einem einzigen Feld
 * zusammen — was nur solange stimmte, wie es genau ein gebautes Modul gab.
 *
 * Seit dem Paket „Menü komplett" (05.09.) ist KEIN Eintrag mehr stumm. Das Feld `fertig`
 * bleibt trotzdem, und mit ihm die Regel „ein noch nicht gebautes Modul steht sichtbar,
 * aber abgeblendet da, damit die Reihenfolge und Vollständigkeit der Navigation von Anfang
 * an feststeht" — sie gilt für den nächsten Eintrag genauso. Die Kürzungsrechnung unten
 * schickt stumme Einträge weiterhin zuerst ins Überlaufmenü (`passtAufteilung`, geprüft in
 * navigationLeiste.test.tsx).
 */
export const BEREICHE: Eintrag[] = [
  { id: "kunden", label: "Kunden", icon: "users", fertig: true, ziel: "kunden" },
  // Angebote und Aufträge sind seit der Etappe „Bereichs-Spalten" vollwertige BEREICHE:
  // Liste links, Detail rechts (views/BereichAngebote.tsx, views/BereichAuftraege.tsx) —
  // der Umweg über die Kundenakte entfällt als Pflicht, bleibt aber als Verweis „Zum
  // Kunden". Beide nicht zu verwechseln mit den gleichnamigen Reitern IN der Akte: Die
  // gliedern EINEN Kunden, diese Punkte den ganzen Bestand.
  { id: "angebote", label: "Angebote", icon: "file-lines", fertig: true,
    ziel: "angebote" },
  { id: "auftraege", label: "Aufträge", icon: "file-contract", fertig: true,
    ziel: "auftraege" },
  // Die RECHNUNGEN schliessen die Kette Angebot → Auftrag → Rechnung und stehen deshalb
  // hinter den Aufträgen (Aufgabe 5b, Entscheid Dirk 06.09.2026). Auch sie sind ein
  // vollwertiger BEREICH (views/BereichRechnungen.tsx) und nicht zu verwechseln mit dem
  // gleichnamigen Reiter IN der Akte: Der gliedert EINEN Kunden, dieser Punkt führt zu
  // jedem Beleg. Auf dem iPhone kürzt sich die untere Leiste über ihr „…"-Überlaufmenü
  // weiterhin selbst, falls die fünf Bereiche auf einem sehr schmalen Gerät nicht passen.
  { id: "rechnungen", label: "Rechnungen", icon: "file-invoice", fertig: true,
    ziel: "rechnungen" },
  // Der Katalog ist LESEND und steht deshalb jedem angemeldeten Bearbeiter offen — anders
  // als „Verwaltung" unten, wo derselbe Bestand gepflegt wird.
  { id: "artikel", label: "Artikel", icon: "box", fertig: true, ziel: "artikel" },
];

// Icon "lock" statt eines eigenen Verwaltungs-Symbols: Das Sprite entsteht aus dem
// vendorierten Font-Awesome-Satz (packages/ui/src/icons/build-sprite.mjs), der in
// diesem Arbeitsstand nicht vorliegt — ein neuer Name ergäbe ein leeres Bild. "lock"
// trifft die Sache ausserdem: Der Eintrag steht nur Administratoren offen.
const VERWALTUNG: Eintrag = {
  id: "verwaltung", label: "Verwaltung", icon: "lock", fertig: true, ziel: "verwaltung",
};

/**
 * Die zweite Gruppe: was hinter dem Zahnrad rechts oben hängt (Entscheid Dirk 10.09.2026).
 *
 * Die Reihenfolge ist die der Spec — Verwaltung, Schnittstellen, Einstellungen: vom
 * Weitesten zum Persönlichsten. Die Verwaltung steht in `einstellungsEintraege` davor,
 * weil sie nur Administratoren überhaupt zu sehen bekommen (siehe dort).
 *
 * „Darstellung" und „Abmelden" gehören NICHT hierher; sie bleiben im Konto-Menü, wo sie
 * seit dem ersten Paket sitzen und wo sie hingehören: Das Zahnrad richtet die ANWENDUNG
 * ein, das Konto den BENUTZER.
 */
export const EINSTELLUNGEN: Eintrag[] = [
  { id: "schnittstellen", label: "Schnittstellen", icon: "plug", fertig: true,
    ziel: "schnittstellen" },
  { id: "einstellungen", label: "Einstellungen", icon: "gear", fertig: true,
    ziel: "einstellungen" },
];

/**
 * Die Einträge des Zahnrad-Menüs für DIESEN Benutzer.
 *
 * Der Verwaltungseintrag fehlt ganz, statt abgeblendet dazustehen: Ein abgeblendeter
 * Eintrag verspricht „das kannst du später", hier geht es aber um eine Berechtigung, die
 * dieser Benutzer nicht hat und nicht selbst bekommt. Der Riegel liegt beim SERVER (403 an
 * jeder Verwaltungsroute, fail closed) — diese Prüfung sorgt nur dafür, dass die
 * Oberfläche niemanden in eine sichere 403 hineinführt.
 *
 * Die Verwaltung steht dabei VORN, nicht am Ende: Die Spec-Reihenfolge ist Verwaltung,
 * Schnittstellen, Einstellungen, und ein Eintrag, der je nach Rolle die Reihenfolge der
 * anderen verschöbe, wäre nicht wiedererkennbar.
 */
export function einstellungsEintraege(istAdmin: boolean, zuschnitt: Zuschnitt = "voll"): Eintrag[] {
  // Im Zuschnitt „belegdesign" fehlen die Schnittstellen: Sie haben mit dem Beleg nichts zu
  // tun. Kosmetik, kein Sicherheitsmerkmal — die Route selbst bleibt erreichbar und prüft
  // ihre Rechte wie immer (siehe zuschnitt.ts).
  const eigene = zuschnitt === "belegdesign"
    ? EINSTELLUNGEN.filter((e) => e.id !== "schnittstellen") : EINSTELLUNGEN;
  return istAdmin ? [VERWALTUNG, ...eigene] : eigene;
}

/** Der eine Bereich des Zuschnitts „belegdesign". Icon aus dem vorhandenen Satz (used.txt). */
const BELEGDESIGN: Eintrag = {
  id: "belegdesign", label: "Belegdesign", icon: "file-invoice", fertig: true,
  ziel: "belegdesign",
};

/**
 * Die BEREICHE für einen Zuschnitt: voll die fünf aus `BEREICHE`, im Zuschnitt
 * „belegdesign" allein der Belegdesigner. Kopfleisten-Reihe und untere Leiste lesen
 * beide hier — dieselbe Regel wie bei `BEREICHE`: eine Quelle, kein zweites Array.
 * Kosmetik, kein Sicherheitsmerkmal (siehe zuschnitt.ts).
 */
export function bereicheFuer(zuschnitt: Zuschnitt): Eintrag[] {
  return zuschnitt === "belegdesign" ? [BELEGDESIGN] : BEREICHE;
}

// ---------------------------------------------------------------------------
// Die Kürzung der unteren Leiste
// ---------------------------------------------------------------------------

/** Der Schlüssel, unter dem die Messleiste die Breite des „…"-Knopfes ablegt. */
const MEHR_MASS = "__mehr";

/** Ein Eintrag, so wie die Aufteilungsrechnung ihn sieht. */
export type EintragsMass = {
  id: string;
  /** Die NATÜRLICHE Breite in Pixeln (gemessen, nicht die gedehnte in der Leiste). */
  breite: number;
  /** Bleibt immer direkt sichtbar: „Kunden" und der Eintrag, auf dem man gerade steht. */
  pflicht: boolean;
  /** Stumm geschaltet (noch nicht gebautes Modul) — wandert zuerst ins Überlaufmenü. */
  stumm: boolean;
};

export type Aufteilung = { sichtbar: string[]; ueberlauf: string[] };

/**
 * Wie viele Einträge der unteren Leiste WIRKLICH nebeneinander passen — und was hinter den
 * „…"-Knopf wandert.
 *
 * Eine reine Funktion, weil das die einzige Stelle ist, an der sich die Regeln prüfen
 * lassen: jsdom layoutet nicht, ein Test an der gerenderten Leiste könnte über
 * Prioritäten und Grenzfälle nichts aussagen (siehe test/navigationLeiste.test.tsx).
 *
 * Die Regeln, in dieser Reihenfolge:
 *  1. Passt alles zusammen, gibt es gar keinen „…"-Knopf (und er kostet dann auch nichts).
 *  2. Sonst kostet er Platz und wird vom Budget abgezogen.
 *  3. `pflicht`-Einträge bleiben IMMER direkt sichtbar — auch wenn sie das Budget
 *     sprengen. Eine Leiste ohne „Kunden" und ohne den Eintrag, auf dem man steht, wäre
 *     schlimmer als eine zu enge Leiste.
 *  4. Vom Rest kommen die bedienbaren vor die stummen: Ein Eintrag, den man ohnehin nicht
 *     antippen kann, darf keinen Platz vor einem wegnehmen, der etwas tut.
 *  5. Ein späterer schmaler Eintrag rückt nach, wenn ein früherer breiter nicht mehr passt
 *     — geprüft wird jeder einzeln, nicht bis zum ersten Fehlschlag.
 *
 * Beide Ergebnislisten stehen in der ursprünglichen Reihenfolge der Leiste: Die Priorität
 * entscheidet, WER mitkommt, nicht, wo er steht — eine Leiste, die ihre Reihenfolge je
 * nach Breite ändert, wäre nicht wiedererkennbar.
 */
export function passtAufteilung(
  eintraege: readonly EintragsMass[], verfuegbar: number, mehrBreite: number,
): Aufteilung {
  const gesamt = eintraege.reduce((summe, e) => summe + e.breite, 0);
  if (gesamt <= verfuegbar) {
    return { sichtbar: eintraege.map((e) => e.id), ueberlauf: [] };
  }

  const budget = verfuegbar - mehrBreite;
  const drin = new Set<string>();
  let belegt = 0;
  for (const e of eintraege) {
    if (!e.pflicht) continue;
    drin.add(e.id);
    belegt += e.breite;
  }
  const rest = [
    ...eintraege.filter((e) => !e.pflicht && !e.stumm),
    ...eintraege.filter((e) => !e.pflicht && e.stumm),
  ];
  for (const e of rest) {
    if (belegt + e.breite > budget) continue;
    drin.add(e.id);
    belegt += e.breite;
  }

  return {
    sichtbar: eintraege.filter((e) => drin.has(e.id)).map((e) => e.id),
    ueberlauf: eintraege.filter((e) => !drin.has(e.id)).map((e) => e.id),
  };
}

// ---------------------------------------------------------------------------
// Die Ansicht
// ---------------------------------------------------------------------------

type TeilProps = {
  eintraege: Eintrag[];
  aktiv: NavigationsZiel;
  onNavigate: (ziel: NavigationsZiel) => void;
};

export function istAktiv(e: Eintrag, aktiv: NavigationsZiel): boolean {
  return e.ziel !== undefined && e.ziel === aktiv;
}

/** Ein Eintrag der unteren Leiste als Knopf. */
function EintragKnopf(
  p: { eintrag: Eintrag; aktiv: NavigationsZiel; onNavigate: (z: NavigationsZiel) => void },
): ReactElement {
  const e = p.eintrag;
  const aktiv = istAktiv(e, p.aktiv);
  return (
    <button
      type="button"
      className={`nv__eintrag${aktiv ? " nv__eintrag--aktiv" : ""}`}
      disabled={!e.fertig}
      aria-current={aktiv ? "page" : undefined}
      title={e.fertig ? undefined : "Kommt in einem späteren Paket."}
      onClick={() => { if (e.ziel !== undefined) p.onNavigate(e.ziel); }}
    >
      <Icon name={e.icon} size={18} />
      <span className="nv__label">{e.label}</span>
    </button>
  );
}

type Messung = { breiten: Record<string, number>; verfuegbar: number };

function gleicheMessung(a: Messung | null, b: Messung): boolean {
  if (a === null || a.verfuegbar !== b.verfuegbar) return false;
  const schluessel = Object.keys(b.breiten);
  if (Object.keys(a.breiten).length !== schluessel.length) return false;
  return schluessel.every((k) => a.breiten[k] === b.breiten[k]);
}

/**
 * Die untere Leiste auf dem iPhone — daumenfreundlich, und sie BLEIBT (Entscheid Dirk
 * 10.09.2026). Sie trägt seither nur noch die fünf BEREICHE; was hinter dem Zahnrad
 * hängt, steht auf dem Telefon in derselben Kopfleiste wie überall sonst. Die
 * Bereichsreihe der Kopfleiste gibt es hier dagegen NICHT: eine zweite Kopfzeile über
 * einer unteren Leiste, die dasselbe sagt, wäre eine Zeile zu viel auf dem kleinsten
 * Bildschirm.
 *
 * Sie ist der einzige Ort, an dem alle Einträge in EINE
 * Zeile müssen — mit sieben Einträgen lief sie über: Der erste stand links ausserhalb des
 * Bildes und die ganze Seite liess sich seitlich schieben.
 *
 * Sie kürzt sich deshalb dynamisch: Was passt, steht direkt da; der Rest wandert hinter
 * einen „…"-Knopf mit einem nach OBEN aufklappenden Menü (die Leiste klebt unten).
 *
 * **Warum eine unsichtbare Messleiste?** Um zu wissen, wie viele Einträge passen, braucht
 * es die natürlichen Breiten ALLER Einträge — auch der gerade nicht gezeigten. Die stehen
 * in einer zweiten, gleich gestalteten Reihe, die absolut positioniert und unsichtbar ist:
 * Sie nimmt keinen Platz ein, lässt sich aber messen. Sie trägt bewusst EIGENE Klassen
 * (`nv__mess-…`) und `aria-hidden`, damit weder CSS-Regeln noch Testabfragen noch
 * Bildschirmleser die Muster mit den echten Knöpfen verwechseln.
 */
export function Tableiste(props: TableisteProps): ReactElement {
  return <Leiste eintraege={props.eintraege ?? BEREICHE} aktiv={props.aktiv}
    onNavigate={props.onNavigate} />;
}

function Leiste(p: TeilProps): ReactElement {
  const navRef = useRef<HTMLElement>(null);
  const messRef = useRef<HTMLDivElement>(null);
  const huelleRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const mehrRef = useRef<HTMLButtonElement>(null);
  const [messung, setMessung] = useState<Messung | null>(null);
  const [menuOffen, setMenuOffen] = useState(false);

  // Die Muster ändern sich nur, wenn ein Eintrag hinzukommt oder wegfällt. Seit die
  // Leiste nur noch die fünf Bereiche trägt (10.09.2026), tut das keiner mehr — die
  // Abhängigkeit bleibt trotzdem stehen: Sie ist die richtige Bedingung, nicht eine, die
  // zufällig gerade konstant ist. Alles andere darf die Messung nicht neu verdrahten.
  const schluessel = p.eintraege.map((e) => e.id).join(",");

  const aufteilung = passtAufteilung(
    p.eintraege.map((e) => ({
      id: e.id,
      breite: messung?.breiten[e.id] ?? 0,
      pflicht: e.id === "kunden" || istAktiv(e, p.aktiv),
      stumm: !e.fertig,
    })),
    messung?.verfuegbar ?? 0,
    messung?.breiten[MEHR_MASS] ?? 0,
  );
  const sichtbar = p.eintraege.filter((e) => aufteilung.sichtbar.includes(e.id));
  const ueberlauf = p.eintraege.filter((e) => aufteilung.ueberlauf.includes(e.id));
  const hatUeberlauf = ueberlauf.length > 0;

  /*
   * Die Messung. `verfuegbar` ist bewusst die VOLLE Breite der Leiste (des <nav>), nicht
   * die der inneren Reihe: Die ist um den „…"-Knopf schmaler, sobald es ihn gibt, und
   * würde damit vom eigenen Ergebnis abhängen. Der Knopf wird stattdessen in
   * `passtAufteilung` als `mehrBreite` vom Budget abgezogen.
   *
   * Die Breite des Knopfes kommt, sobald er wirklich dasteht, vom KNOPF SELBST (mehrRef) —
   * nur die kann nicht von seiner Darstellung abweichen. Vor seinem ersten Erscheinen
   * bleibt das Muster in der Messreihe die Schätzung; es teilt sich dafür seine CSS-Regel
   * mit dem echten Knopf (siehe `.nv__mehr, .nv__mess-mehr` in Navigation.css). Deshalb
   * hängt dieser Effekt auch an `hatUeberlauf`: Erscheint der Knopf, wird mit seiner
   * echten Breite genau einmal nachgerechnet. Ein Hin und Her kann daraus nicht werden —
   * ob es überhaupt einen Überlauf gibt, entscheidet allein die Summe aller Einträge
   * gegen die verfügbare Breite, und die kennt `mehrBreite` gar nicht.
   */
  useLayoutEffect(() => {
    const rahmen = navRef.current;
    const messreihe = messRef.current;
    if (rahmen === null || messreihe === null) return undefined;

    // Beide Elemente als Argumente statt als eingefangene Variablen: Der Beobachter ruft
    // `messen` später erneut, und der Typprüfer trägt eine Einengung auf „nicht null"
    // nicht bis dorthin.
    function messen(leiste: HTMLElement, muster: HTMLElement): void {
      const breiten: Record<string, number> = {};
      for (const feld of Array.from(
        muster.querySelectorAll<HTMLElement>("[data-nv-mass]"))) {
        const id = feld.getAttribute("data-nv-mass");
        if (id !== null) breiten[id] = feld.offsetWidth;
      }
      const echterKnopf = mehrRef.current;
      if (echterKnopf !== null) breiten[MEHR_MASS] = echterKnopf.offsetWidth;
      const neu: Messung = { breiten, verfuegbar: leiste.clientWidth };
      // Nur bei echter Änderung neu zeichnen — sonst liefe die Messung im Kreis.
      setMessung((alt) => (gleicheMessung(alt, neu) ? alt : neu));
    }

    messen(rahmen, messreihe);
    const beobachter = new ResizeObserver(() => messen(rahmen, messreihe));
    beobachter.observe(rahmen);
    return () => beobachter.disconnect();
  }, [schluessel, hatUeberlauf]);

  // Wird die Leiste breiter, verschwindet der „…"-Knopf — ein offenes Menü ohne Knopf
  // darunter bliebe sonst als Rest stehen.
  useEffect(() => {
    if (!hatUeberlauf) setMenuOffen(false);
  }, [hatUeberlauf]);

  /*
   * Das Menü schliesst bei Escape und beim Tippen daneben.
   *
   * Escape läuft über den gemeinsamen ESCAPE-STAPEL (@kundeninfo/ui, useEscapeEbene) und
   * nicht mehr über einen eigenen Horcher am Dokument. Für DIESES Menü war das nie ein
   * Problem — die untere Leiste gibt es nur auf dem Telefon, und dort hat der Rahmen weder
   * überlagernde Ränder noch einen eigenen Escape-Horcher. Es hängt trotzdem am selben
   * Stapel wie die beiden Menüs der Kopfleiste: Eine Schicht, die sich nicht anmeldet, ist
   * genau der Einzelhorcher, aus dem der Stapel herausführen soll, und das Zahnrad der
   * Kopfleiste steht auf dem Telefon unmittelbar darüber.
   *
   * Der Fokus geht bei Escape an den „…"-Knopf zurück — wer mit der Tastatur öffnet, soll
   * nicht am Seitenanfang landen.
   *
   * Das TASTATURMUSTER (Fokus beim Aufklappen, Pfeile mit Umlauf, Home/End, roving
   * tabindex) kommt aus demselben Baustein wie bei den Kopfleisten-Menüs — siehe
   * useMenuTasten. Bewusst OHNE Fokusfalle: Ein Überlaufmenü ist kein Dialog (dieselbe
   * Abgrenzung wie bei den überlagernden Rändern in MeSchale.tsx).
   *
   * Der Horcher für das Tippen daneben hängt am Dokument, damit er auch greift, wenn der
   * Fokus nirgendwo steht (der Normalfall nach einer Tippgeste).
   */
  useEscapeEbene(menuOffen, () => {
    setMenuOffen(false);
    mehrRef.current?.focus();
  });
  // Tab schliesst OHNE Fokusrückgabe (siehe useMenuTasten) — anders als Escape darüber.
  useMenuTasten(menuOffen, menuRef, mehrRef, () => setMenuOffen(false));

  useEffect(() => {
    if (!menuOffen) return undefined;
    function beiZeiger(ereignis: MouseEvent): void {
      const huelle = huelleRef.current;
      if (huelle !== null && ereignis.target instanceof Node
        && huelle.contains(ereignis.target)) return;
      setMenuOffen(false);
    }
    document.addEventListener("mousedown", beiZeiger);
    return () => document.removeEventListener("mousedown", beiZeiger);
  }, [menuOffen]);

  return (
    <nav className="nv nv--leiste" aria-label="Hauptnavigation" ref={navRef}>
      <div className="nv__reihe">
        <div className="nv__messung" aria-hidden="true" ref={messRef}>
          {p.eintraege.map((e) => (
            <span key={e.id} className="nv__mess-eintrag" data-nv-mass={e.id}>
              <Icon name={e.icon} size={18} />
              <span className="nv__label">{e.label}</span>
            </span>
          ))}
          {/* Das Muster des „…"-Knopfes. Es trägt NICHT die Eintrags-Klasse, sondern
              teilt sich seine Regel mit dem echten Knopf (`.nv__mehr, .nv__mess-mehr`) —
              sonst wäre es schmaler als er und das Platzbudget systematisch zu
              grosszügig. Gebraucht wird es nur, bis der Knopf das erste Mal dasteht;
              danach misst der Effekt oben ihn selbst. */}
          <span className="nv__mess-mehr" data-nv-mass={MEHR_MASS}>…</span>
        </div>
        {sichtbar.map((e) => (
          <EintragKnopf key={e.id} eintrag={e} aktiv={p.aktiv} onNavigate={p.onNavigate} />
        ))}
      </div>

      {hatUeberlauf && (
        <div className="nv__mehr-huelle" ref={huelleRef}>
          <button
            type="button"
            className="nv__mehr"
            ref={mehrRef}
            aria-label="Weitere"
            aria-haspopup="menu"
            aria-expanded={menuOffen}
            onClick={() => setMenuOffen((offen) => !offen)}
          >
            <span aria-hidden="true">…</span>
          </button>
          {menuOffen && (
            <div className="nv__menu" role="menu" aria-label="Weitere Navigationspunkte"
                 ref={menuRef}>
              {ueberlauf.map((e) => {
                const aktiv = istAktiv(e, p.aktiv);
                return (
                  <button
                    key={e.id}
                    type="button"
                    role="menuitem"
                    className={`nv__menu-eintrag${aktiv ? " nv__menu-eintrag--aktiv" : ""}`}
                    disabled={!e.fertig}
                    aria-current={aktiv ? "page" : undefined}
                    title={e.fertig ? undefined : "Kommt in einem späteren Paket."}
                    onClick={() => {
                      setMenuOffen(false);
                      if (e.ziel !== undefined) p.onNavigate(e.ziel);
                    }}
                  >
                    <Icon name={e.icon} size={18} />
                    <span className="nv__label">{e.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </nav>
  );
}
